import { memo, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Heading, Icon, LoadingMask, Text } from '@zillow/constellation-10';
import { IconSparkleOutline } from '@zillow/constellation-icons';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { getListingStatusSuffix } from '../../../../utils/clickstreamUtils';

import { REVIEW_PAGE_SECTIONS } from '../../../../constants/ListingDetailConstants';

import SpecialOffersModal from '../../../modals/SpecialOffersModal';
import SavedSpecialOfferCard from './components/SavedSpecialOfferCard';

import { emitSpecialOffersAddNewEvent, EVENT_IDS } from '../../../../analytics/events/specialOffers';
import { useAuth } from '../../../AuthProvider';
import RecommendationsAlert from '../../../PostingPath/RecommendationsAlert';

type Props = {
  onRecsClick: (recommendationType: string) => void;
};

const SpecialOffersContent = ({ onRecsClick }: Props): JSX.Element => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails, isFetching: isFetchingListingDetails } = useGetListingDetails(listingAlias);

  const [showSpecialOffersModal, setShowSpecialOffersModal] = useState(false);
  const [firstClickToAddOffer, setFirstClickToAddOffer] = useState(false);

  const specialOffers = listingDetails?.specialOffers || [];
  const isArchived = listingDetails?.isArchived || false;
  const isUpdateListingRestricted = listingDetails?.restrictions?.isUpdateListingRestricted || false;
  const isUpdateSpecialOffersRestricted = listingDetails?.restrictions?.isUpdateSpecialOffersRestricted || false;
  const isSelfServeOnboardingInProgress =
    listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress || false;
  const isSSOBUpdateRestricted = listingDetails?.selfServeOnboardingStatus?.isUpdateRestricted || false;

  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const isInputFormDisabled =
    isUpdateSpecialOffersRestricted ||
    !canUpdateListing ||
    isArchived ||
    isUpdateListingRestricted ||
    (isSelfServeOnboardingInProgress && isSSOBUpdateRestricted);

  // Track when a user with no offers starts to add an offer (first click only)
  useEffect(() => {
    if (firstClickToAddOffer && user && listingDetails && specialOffers.length === 0) {
      const triggerLocationSuffix = getListingStatusSuffix(
        listingDetails.active,
        listingDetails.lastActivatedMs?.toString(),
      );
      emitSpecialOffersAddNewEvent({
        currentProperty: listingDetails,
        user,
        triggerLocation: `multifamily_review_page_special_offers_empty_state${triggerLocationSuffix}`,
      });
      setFirstClickToAddOffer(false);
    }
  }, [firstClickToAddOffer, user, listingDetails, specialOffers.length]);

  const onAddSpecialOfferClick = () => {
    if (specialOffers.length === 0) {
      setFirstClickToAddOffer(true);
    }
    setShowSpecialOffersModal(true);
  };

  return (
    <div data-testid="SpecialOffersContent-Edit">
      <RecommendationsAlert sectionName={REVIEW_PAGE_SECTIONS.SPECIAL_OFFERS} onRecsClick={onRecsClick} />

      <LoadingMask.Root loading={isFetchingListingDetails} css={{ width: '100%' }}>
        <LoadingMask.Content css={{ width: '100%' }}>
          {specialOffers.length <= 0 ? (
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
              <Box
                css={{
                  minHeight: '240px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '400',
                  flex: '1',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Icon size="md" render={<IconSparkleOutline />} css={{ color: 'icon.subtle' }} />

                <Heading textStyle="heading-xs" level={6}>
                  No special offers added
                </Heading>
                <Text>Add a special offer to attract renters</Text>

                {!isUpdateSpecialOffersRestricted && (
                  <Box>
                    <Button
                      disabled={isSelfServeOnboardingInProgress && isSSOBUpdateRestricted}
                      onClick={onAddSpecialOfferClick}
                      data-eventid={EVENT_IDS.SPECIAL_OFFERS_ADD_NEW}
                    >
                      Add special offer
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box css={{ display: 'flex', gap: '600', flexDirection: 'column' }}>
              {specialOffers.map((offer, i) => (
                <SavedSpecialOfferCard
                  key={offer.offerIdEncoded || i}
                  index={i}
                  specialOffer={offer}
                  isInputFormDisabled={isInputFormDisabled}
                  listingAlias={listingAlias}
                />
              ))}

              {!isInputFormDisabled && specialOffers.length < 5 && (
                <Box css={{ display: 'flex' }}>
                  <Button
                    disabled={isSelfServeOnboardingInProgress && isSSOBUpdateRestricted}
                    onClick={onAddSpecialOfferClick}
                  >
                    Add another offer
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </LoadingMask.Content>
        <LoadingMask.Spinner />
      </LoadingMask.Root>

      {showSpecialOffersModal && (
        <SpecialOffersModal isOpen={showSpecialOffersModal} onClose={() => setShowSpecialOffersModal(false)} />
      )}
    </div>
  );
};

export default memo(SpecialOffersContent);
